import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useApp } from '../context/AppContext';
import { User, Scan, Heart, Settings, HelpCircle, MessageSquare, LogOut, ChevronRight, MapPin, Check, Edit3, Users, ShoppingBag, Star, Mail, Chrome, Phone, X, Shield } from 'lucide-react';

const COUNTRIES = [
  { code: 'India', flag: '🇮🇳', currency: 'INR', symbol: '₹' },
  { code: 'USA', flag: '🇺🇸', currency: 'USD', symbol: '$' },
  { code: 'UK', flag: '🇬🇧', currency: 'GBP', symbol: '£' },
  { code: 'UAE', flag: '🇦🇪', currency: 'AED', symbol: 'AED' }
];

const ProfileScreen = () => {
  const { user, logout, updateProfile } = useAuth();
  const { scanHistory, wishlist, setCurrentScreen, setActiveTab, selectedCountry, setCountry } = useApp();
  const [showCountrySelector, setShowCountrySelector] = useState(false);

  const handleCountrySelect = (country) => {
    setCountry(country);
    setShowCountrySelector(false);
  };

  const currentCountry = COUNTRIES.find(c => c.code === selectedCountry) || COUNTRIES[0];

  // A real name is one the user set, not the phone-number / email-prefix fallback.
  const hasName = !!(user?.name && user.name !== 'Guest' && user.name !== user.phone
    && !(user.email && user.name === user.email.split('@')[0]));
  const isAdmin = user?.role === 'admin';
  const initials = (hasName ? user.name : (user?.email || user?.phone || 'A'))
    .replace(/[^A-Za-z0-9]/g, ' ').trim().split(/\s+/).slice(0, 2).map(s => s[0]).join('').toUpperCase() || 'A';

  const [showEdit, setShowEdit] = useState(false);
  const [editName, setEditName] = useState('');
  const [editPhone, setEditPhone] = useState('');
  const [saving, setSaving] = useState(false);
  const [editErr, setEditErr] = useState('');

  const openEdit = () => {
    setEditName(hasName ? user.name : '');
    setEditPhone(user?.phone || '');
    setEditErr('');
    setShowEdit(true);
  };

  const saveEdit = async () => {
    if (editName.trim().length < 2) { setEditErr('Please enter your name.'); return; }
    setSaving(true); setEditErr('');
    const res = await updateProfile({ name: editName.trim(), phone: editPhone.trim() });
    setSaving(false);
    if (res.success) setShowEdit(false);
    else setEditErr(res.error || 'Could not save. Try again.');
  };

  const stats = [
    { label: 'Scans', value: Math.max(user?.scan_count || 0, scanHistory?.length || 0), icon: Scan },
    { label: 'Wishlist', value: wishlist.length, icon: Heart }
  ];

  const preferencesItems = [
    { 
      label: 'Edit Likeness', 
      icon: User, 
      action: () => setCurrentScreen('edit-likeness'),
      desc: 'Body type, measurements for try-on'
    },
    { 
      label: 'Followed Brands', 
      icon: Star, 
      action: () => setCurrentScreen('followed-brands'),
      desc: 'Curate your preferred brands'
    },
    { 
      label: 'Shopping Preferences', 
      icon: ShoppingBag, 
      action: () => setCurrentScreen('shopping-preferences'),
      desc: 'Fabric types, sustainability priorities'
    }
  ];

  const menuItems = [
    { label: 'Settings', icon: Settings, action: () => setCurrentScreen('app-settings') },
    { label: 'Account', icon: User, action: () => setCurrentScreen('account') },
    { label: 'Help', icon: HelpCircle, action: () => setCurrentScreen('help') },
    { label: 'Review App', icon: Star, action: () => alert('Review form coming soon') },
    { label: 'Contact Us', icon: Mail, action: () => setCurrentScreen('contact-us') },
    { label: 'Feedback', icon: MessageSquare, action: () => setCurrentScreen('contact-us') }
  ];

  return (
    <div className="min-h-screen bg-white pb-24">
      {/* Header with Back Arrow + Large Logo */}
      <div className="px-6 pt-6 pb-6 flex items-center gap-4">
        <button
          onClick={() => setActiveTab('home')}
          className="w-10 h-10 bg-black/5 rounded-xl flex items-center justify-center hover:bg-black/10 transition-all flex-shrink-0"
        >
          <ChevronRight size={18} className="text-black rotate-180" />
        </button>
        <img 
          src="/logos/logo-black.jpg" 
          alt="ATELIA" 
          className="h-24 w-auto object-contain"
        />
      </div>

      {/* User Identity Card */}
      <div className="px-6 mb-6">
        <div className="bg-white border border-black/12 rounded-2xl p-6 text-center">
          <div className="w-24 h-24 rounded-full mx-auto mb-4 overflow-hidden bg-black flex items-center justify-center">
            {user?.photoURL ? (
              <img src={user.photoURL} alt="" className="w-full h-full object-cover" referrerPolicy="no-referrer" />
            ) : (
              <span className="text-white text-2xl font-bold tracking-wide">{initials}</span>
            )}
          </div>
          <div className="flex items-center justify-center gap-2">
            <h2 className="text-xl font-bold text-black uppercase tracking-wide">
              {hasName ? user.name : 'Add your name'}
            </h2>
            {isAdmin && (
              <span className="inline-flex items-center gap-1 text-[9px] px-2 py-0.5 rounded-full font-bold uppercase tracking-wide"
                style={{ background: '#800020', color: '#fff' }}>
                <Shield size={10} /> Admin
              </span>
            )}
          </div>

          {/* Contact rows */}
          <div className="mt-3 space-y-1.5">
            {user?.email && (
              <p className="text-sm text-black/60 flex items-center justify-center gap-1.5">
                <Mail size={13} className="text-black/40" /> {user.email}
              </p>
            )}
            {user?.phone ? (
              <p className="text-sm text-black/60 flex items-center justify-center gap-1.5">
                <Phone size={13} className="text-black/40" /> {user.phone}
              </p>
            ) : (
              <p className="text-sm text-black/35 flex items-center justify-center gap-1.5">
                <Phone size={13} className="text-black/30" /> No phone added
              </p>
            )}
          </div>

          <button
            onClick={openEdit}
            className="mt-4 text-xs text-black/55 hover:text-black flex items-center gap-1 mx-auto uppercase font-semibold"
          >
            <Edit3 size={12} />
            Edit Profile
          </button>
        </div>

        {/* Complete-your-profile nudge (name missing) */}
        {!hasName && (
          <button
            onClick={openEdit}
            className="mt-3 w-full rounded-xl p-4 flex items-center justify-between text-left transition-all"
            style={{ background: 'rgba(128,0,32,0.06)', border: '1px solid rgba(128,0,32,0.25)' }}
          >
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: '#800020' }}>
                <User size={16} className="text-white" />
              </div>
              <div>
                <p className="text-sm font-bold" style={{ color: '#800020' }}>Complete your profile</p>
                <p className="text-xs text-black/55 mt-0.5">Add your name so Atelia can greet you.</p>
              </div>
            </div>
            <ChevronRight size={16} style={{ color: '#800020' }} />
          </button>
        )}
      </div>

      {/* Edit Profile Modal */}
      {showEdit && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center px-6"
          onClick={() => !saving && setShowEdit(false)}>
          <div className="bg-white rounded-2xl w-full max-w-sm p-6" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-lg font-bold text-black uppercase tracking-wide">Edit Profile</h2>
              <button onClick={() => !saving && setShowEdit(false)} className="text-black/40 hover:text-black">
                <X size={20} />
              </button>
            </div>

            <label className="block text-[11px] uppercase tracking-[0.15em] text-black/50 font-bold mb-1.5">Name</label>
            <input
              value={editName}
              onChange={e => setEditName(e.target.value)}
              placeholder="e.g. Aisha Kapoor"
              className="w-full h-12 px-4 rounded-xl border border-black/15 focus:border-black outline-none text-black mb-4"
              autoFocus
            />

            <label className="block text-[11px] uppercase tracking-[0.15em] text-black/50 font-bold mb-1.5">
              Phone {user?.phone ? '' : '(optional)'}
            </label>
            <input
              value={editPhone}
              onChange={e => setEditPhone(e.target.value)}
              type="tel"
              inputMode="tel"
              placeholder="+91 80000 00000"
              className="w-full h-12 px-4 rounded-xl border border-black/15 focus:border-black outline-none text-black"
            />
            {user?.email && (
              <p className="text-[11px] text-black/40 mt-1.5">Signed in with Google · {user.email}</p>
            )}

            {editErr && <p className="text-xs text-red-600 mt-3">{editErr}</p>}

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShowEdit(false)}
                disabled={saving}
                className="flex-1 py-3 bg-white border border-black/12 rounded-xl text-sm font-bold uppercase hover:bg-black/5 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={saveEdit}
                disabled={saving}
                className="flex-1 py-3 text-white rounded-xl text-sm font-bold uppercase disabled:opacity-50"
                style={{ background: '#800020' }}
              >
                {saving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Stats */}
      <div className="px-6 mb-6">
        <p className="text-xs uppercase tracking-[0.2em] text-black/55 mb-3 font-bold">YOUR STATS</p>
        <div className="grid grid-cols-2 gap-3">
          {stats.map((stat, i) => {
            const Icon = stat.icon;
            return (
              <div key={i} className="bg-white border border-black/12 rounded-xl p-4 text-center">
                <Icon size={20} strokeWidth={1.5} className="mx-auto text-black/55 mb-2" />
                <p className="text-2xl font-bold text-black">{stat.value}</p>
                <p className="text-xs text-black/55 mt-1 uppercase font-semibold">{stat.label}</p>
              </div>
            );
          })}
        </div>
      </div>

      {/* Install Extension - Beta Placeholder */}
      <div className="px-6 mb-6">
        <button
          onClick={() => alert('Chrome extension coming soon!')}
          className="w-full bg-black/5 border border-black/12 rounded-xl p-4
            flex items-center justify-between hover:bg-black/10 transition-all relative"
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-black/10 rounded-xl flex items-center justify-center">
              <Chrome size={18} strokeWidth={1.5} className="text-black" />
            </div>
            <div className="text-left">
              <div className="flex items-center gap-2">
                <span className="text-sm font-bold text-black uppercase">Install Extension</span>
                <span className="text-[9px] px-1.5 py-0.5 bg-black text-white rounded uppercase font-bold">Beta</span>
              </div>
              <p className="text-xs text-black/55 mt-0.5">Quick scan from your browser</p>
            </div>
          </div>
          <ChevronRight size={16} className="text-black/30" />
        </button>
      </div>

      {/* Invites - Beta Placeholder */}
      <div className="px-6 mb-6">
        <button
          onClick={() => alert('Invites feature coming soon!')}
          className="w-full bg-white border border-black/12 rounded-xl p-4
            flex items-center justify-between hover:bg-black/5 transition-all relative"
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-black/10 rounded-xl flex items-center justify-center relative">
              <Users size={18} strokeWidth={1.5} className="text-black" />
              {/* Red notification dot */}
              <span className="absolute -top-0.5 -right-0.5 w-2 h-2 bg-red-500 rounded-full"></span>
            </div>
            <div className="text-left">
              <div className="flex items-center gap-2">
                <span className="text-sm font-bold text-black uppercase">Invites</span>
                <span className="text-[9px] px-1.5 py-0.5 bg-black text-white rounded uppercase font-bold">Beta</span>
              </div>
              <p className="text-xs text-black/55 mt-0.5">Collaborate with stylists & friends</p>
            </div>
          </div>
          <ChevronRight size={16} className="text-black/30" />
        </button>
      </div>

      {/* Shopping Location Setting */}
      <div className="px-6 mb-6">
        <p className="text-xs uppercase tracking-[0.2em] text-black/55 mb-3 font-bold">SHOPPING LOCATION</p>
        <button
          onClick={() => setShowCountrySelector(!showCountrySelector)}
          className="w-full px-4 py-3 bg-white border border-black/12 rounded-xl
            flex items-center justify-between hover:border-black/30 transition-all"
        >
          <div className="flex items-center gap-3">
            <span className="text-2xl">{currentCountry.flag}</span>
            <div className="text-left">
              <p className="text-sm font-bold text-black uppercase">{currentCountry.code}</p>
              <p className="text-xs text-black/55 uppercase">Currency: {currentCountry.symbol} {currentCountry.currency}</p>
            </div>
          </div>
          <MapPin size={16} className="text-black/55" />
        </button>

        {/* Country Selector Dropdown */}
        {showCountrySelector && (
          <div className="mt-2 bg-white border border-black/12 rounded-xl overflow-hidden shadow-lg">
            {COUNTRIES.map((country) => (
              <button
                key={country.code}
                onClick={() => handleCountrySelect(country.code)}
                className={`w-full px-4 py-3 flex items-center justify-between hover:bg-black/5 transition-all
                  ${selectedCountry === country.code ? 'bg-black/5' : ''}`}
              >
                <div className="flex items-center gap-3">
                  <span className="text-2xl">{country.flag}</span>
                  <div className="text-left">
                    <p className="text-sm font-bold text-black uppercase">{country.code}</p>
                    <p className="text-xs text-black/55 uppercase">{country.symbol} {country.currency}</p>
                  </div>
                </div>
                {selectedCountry === country.code && (
                  <Check size={16} className="text-black" />
                )}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Preferences Section */}
      <div className="px-6 mb-6">
        <p className="text-xs uppercase tracking-[0.2em] text-black/55 mb-3 font-bold">PREFERENCES</p>
        <div className="space-y-2">
          {preferencesItems.map((item, i) => {
            const Icon = item.icon;
            return (
              <button
                key={i}
                onClick={item.action}
                className="w-full bg-white border border-black/12 rounded-xl p-4
                  flex items-center justify-between hover:bg-black/5 transition-all"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-black/5 rounded-xl flex items-center justify-center">
                    <Icon size={18} strokeWidth={1.5} className="text-black" />
                  </div>
                  <div className="text-left flex-1">
                    <p className="text-sm font-bold text-black uppercase">{item.label}</p>
                    <p className="text-xs text-black/55 mt-0.5">{item.desc}</p>
                  </div>
                </div>
                <ChevronRight size={16} className="text-black/30" />
              </button>
            );
          })}
        </div>
      </div>

      {/* Menu Items (Settings, Account, Support) */}
      <div className="px-6 mb-6">
        <p className="text-xs uppercase tracking-[0.2em] text-black/55 mb-3 font-bold">ACCOUNT & SUPPORT</p>
        <div className="space-y-2">
          {menuItems.map((item, i) => {
            const Icon = item.icon;
            return (
              <button
                key={i}
                onClick={item.action}
                className="w-full bg-white border border-black/12 rounded-xl p-4
                  flex items-center justify-between hover:bg-black/5 transition-all"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-black/5 rounded-xl flex items-center justify-center">
                    <Icon size={18} strokeWidth={1.5} className="text-black" />
                  </div>
                  <span className="text-sm font-bold text-black uppercase">{item.label}</span>
                </div>
                <ChevronRight size={16} className="text-black/30" />
              </button>
            );
          })}
        </div>
      </div>

      {/* Sign Out */}
      {user && (
        <div className="px-6 mt-6">
          <button
            onClick={logout}
            className="w-full py-3 border border-black/12 rounded-xl
              flex items-center justify-center gap-2 text-black/72 hover:text-black hover:bg-black/5 transition-all"
          >
            <LogOut size={16} />
            <span className="text-sm font-semibold uppercase">Sign Out</span>
          </button>
        </div>
      )}
    </div>
  );
};

export default ProfileScreen;